import styled from '@emotion/styled';

import { Button } from '@travelnest/hui';

import { Divider } from '@/components/ui/Divider';
import { Stack } from '@/components/ui/Stack';
import { Text } from '@/components/ui/Text';
import { calculatePricePerNight, formatMoney } from '@/lib/currency';
import { hasSelectedStay } from '@/lib/stay';
import { colors, font } from '@/lib/theme';
import { useI18n } from '@/locales';

import { CancellationPolicy } from './CancellationPolicy';
import { DiscountAlert } from './DiscountCode';
import { PriceBreakdown, PriceBreakdownPlaceholder } from './PriceBreakdown';
import { SecurityDepositNote } from './SecurityDepositNote';
import { DateFields, GuestsField } from './StayFields';
import type { StayPricing } from './useStayPricing';

const PriceLine = styled.p`
	display: flex;
	align-items: baseline;
	gap: 6px;
	margin: 0;
	${font('IR100')}
	color: ${colors.typography.body};

	strong {
		${font('RWB130')}
		color: ${colors.typography.heading};
	}
`;

interface BookingCardProps {
	pricing: StayPricing;
	cancellationPolicy: string;
	securityDeposit: number;
	onOpenCalendar: () => void;
	onOpenGuests: () => void;
	onReserve: () => void;
	/** Label for the CTA once a stay is priced; the sheet on mobile says "Save". */
	reserveLabel?: string;
}

/**
 * The booking panel: pick dates and guests, see the price, reserve. The same
 * card sits in the right-hand column on desktop and inside the bottom sheet
 * on phones.
 */
export const BookingCard = ({
	pricing,
	cancellationPolicy,
	securityDeposit,
	onOpenCalendar,
	onOpenGuests,
	onReserve,
	reserveLabel,
}: BookingCardProps) => {
	const t = useI18n();
	const { stay, currency, discount, discountExceedsTotal } = pricing;
	const selected = hasSelectedStay(stay);
	const [perNight] = calculatePricePerNight(
		stay.startDate,
		stay.endDate,
		stay.baseStayPrice ?? 0,
	);

	return (
		<Stack gap="space40" data-testid="booking-card">
			<PriceLine>
				{selected ? (
					<>
						<strong>{formatMoney(perNight, currency)}</strong>
						<span>{t('booking.price.nightSuffix')}</span>
					</>
				) : (
					<Text as="span" color="heading" weight="medium">
						{t('booking.addDatesForPrice')}
					</Text>
				)}
			</PriceLine>

			{discount !== undefined && (
				<DiscountAlert
					discount={discount}
					currency={currency}
					exceedsTotal={discountExceedsTotal}
				/>
			)}

			<Stack gap="space20">
				<DateFields
					startDate={stay.startDate}
					endDate={stay.endDate}
					onClick={onOpenCalendar}
				/>
				<GuestsField
					adults={stay.adults}
					childCount={stay.children}
					onClick={onOpenGuests}
				/>
			</Stack>

			{selected ? (
				<PriceBreakdown
					stay={stay}
					discountInvalid={discountExceedsTotal}
				/>
			) : (
				<PriceBreakdownPlaceholder stay={stay} />
			)}

			<Stack gap="space20">
				<Button
					text={reserveLabel ?? t('booking.reserve')}
					variant="primary"
					size="large"
					fullWidth
					disabled={discountExceedsTotal}
					onClick={selected ? onReserve : onOpenCalendar}
				/>
				{selected && (
					<Text variant="bodySmall" align="center" color="muted">
						{t('booking.notChargedYet')}
					</Text>
				)}
			</Stack>

			{selected && stay.startDate && (
				<>
					<Divider />
					<Stack gap="space20">
						<CancellationPolicy
							policy={cancellationPolicy}
							checkIn={stay.startDate}
						/>
						<SecurityDepositNote
							currency={currency}
							securityDeposit={securityDeposit}
						/>
					</Stack>
				</>
			)}
		</Stack>
	);
};
